import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
type Point = {
  x: number;
  y: number;
};
const FULL_WIDTH_RULES = '.nav,.hero,.pix,.statband,.quoteband,.split,.stoprow,.grid2,.grid3,.grid4,.keep,.ctaband,.ledger,footer,.faq,.blist,.pager,.partl,.pnav';
const VERTICAL_DIVIDERS = '.cell,.col-l,.sp-l,.pnavc';

/**
 * Dispatched by anything that changes layout inside a user interaction (FAQ
 * disclosures, for one). Handled synchronously so the marks move in the same
 * frame as the content rather than trailing it.
 */
export const REMEASURE_EVENT = 'synthia:remeasure';

/**
 * Layout offsets, not rects. A mark's `top` is resolved against the same
 * layout box the rules are painted into, so reading layout offsets keeps the
 * two in lockstep. Rects are in viewport space and carry the current scroll
 * position, which reintroduces sub-pixel drift on a scrolled page.
 */
function layoutTop(el: HTMLElement, root: HTMLElement): number {
  let y = 0;
  let node: HTMLElement | null = el;
  while (node && node !== root) {
    y += node.offsetTop;
    node = node.offsetParent as HTMLElement | null;
  }
  return y;
}
function layoutLeft(el: HTMLElement, root: HTMLElement): number {
  let x = 0;
  let node: HTMLElement | null = el;
  while (node && node !== root) {
    x += node.offsetLeft;
    node = node.offsetParent as HTMLElement | null;
  }
  return x;
}

/** Centre of a hairline rule drawn just above/before a given layout edge. */
function ruleBefore(edge: number): number {
  return edge <= 0.5 ? 0.5 : edge - 0.5;
}
export function Crosshairs({
  pageRef,
  routeKey
}: {
  pageRef: React.RefObject<HTMLDivElement>;
  routeKey: string;
}) {
  const [points, setPoints] = useState<Point[]>([]);
  const signature = useRef('');
  const measure = useCallback(() => {
    const page = pageRef.current;
    if (!page) return;
    const left = 0.5;
    const right = ruleBefore(page.offsetWidth);

    /**
     * Only top-level sections draw a full-width rule. A block matching the same
     * selector while nested inside one (a grid inside a section, say) has edges
     * that aren't rules at all, and marking them puts stray crosses partway
     * down the frame. Sections are direct children of .page or of its <main>.
     */
    const blocks = Array.from(page.querySelectorAll<HTMLElement>(FULL_WIDTH_RULES)).filter(el => {
      const parent = el.parentElement;
      return parent === page || parent?.tagName === 'MAIN' && parent.parentElement === page;
    });
    const tops = blocks.map(el => layoutTop(el, page)).sort((a, b) => a - b);

    /**
     * Stacked blocks share their rules: one block's bottom border IS the next
     * block's top border. Snapping a computed bottom onto a known top keeps a
     * single intersection from collecting two marks a pixel apart when the
     * content inside resolves to a fractional height — which is what made a
     * disclosure judder.
     */
    const snap = (y: number) => {
      const near = tops.find(t => Math.abs(t - y) <= 1.5);
      return near ?? y;
    };

    /**
     * A mark means "two rules cross here", so it may only sit on a horizontal
     * rule the page actually paints: the boundary between stacked sections, or
     * the very bottom of the frame. Anything else — an inner edge, a stale
     * measurement — is silently dropped rather than left floating on the frame.
     */
    const boundaries = [...tops, page.offsetHeight];
    const onRule = (y: number) => boundaries.some(b => Math.abs(b - y) <= 1.5);
    const seen = new Map<string, Point>();
    const add = (x: number, y: number) => {
      seen.set(`${x.toFixed(2)}|${y.toFixed(2)}`, {
        x,
        y
      });
    };
    const addIfRule = (x: number, edge: number) => {
      if (onRule(edge)) add(x, ruleBefore(edge));
    };
    blocks.forEach(el => {
      const top = layoutTop(el, page);
      const bottom = snap(top + el.offsetHeight);
      addIfRule(left, top);
      addIfRule(right, top);
      addIfRule(left, bottom);
      addIfRule(right, bottom);
    });
    page.querySelectorAll<HTMLElement>(VERTICAL_DIVIDERS).forEach(el => {
      const x = layoutLeft(el, page);
      const y = layoutTop(el, page);
      const x0 = ruleBefore(x);
      const x1 = ruleBefore(x + el.offsetWidth);
      const y0 = y;
      const y1 = snap(y + el.offsetHeight);
      if (onRule(y0)) {
        add(x0, ruleBefore(y0));
        add(x1, ruleBefore(y0));
      }
      if (onRule(y1)) {
        add(x0, ruleBefore(y1));
        add(x1, ruleBefore(y1));
      }
    });
    const next = Array.from(seen.values());
    const nextSignature = Array.from(seen.keys()).sort().join(',');
    if (nextSignature === signature.current) return;
    signature.current = nextSignature;
    setPoints(next);
  }, [pageRef]);

  /**
   * On a route change the previous page's marks are still mounted while the new
   * page lays out. Drop them in the same commit as the new markup and re-place
   * them from the new layout, so they never paint against the wrong rules.
   */
  useLayoutEffect(() => {
    signature.current = '';
    setPoints([]);
    measure();
  }, [routeKey, measure]);
  useEffect(() => {
    let live = true;
    let frame = 0;
    let timer: number | undefined;

    // Content-driven remeasures run on the next frame: a debounce leaves the
    // marks sitting on stale positions long enough to be seen as a jump.
    const soon = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => live && measure());
    };
    const debounced = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(soon, 120);
    };
    // React schedules state updates; left to itself the new marks would land
    // in a later frame than the content that moved them. flushSync commits
    // them inside this same frame, before the browser paints.
    const now = () => {
      if (!live) return;
      flushSync(() => measure());
    };
    window.addEventListener('resize', debounced);
    document.addEventListener(REMEASURE_EVENT, now);

    // Content height changes (route changes, FAQ disclosures) move the rules.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(soon) : null;
    if (observer && pageRef.current) observer.observe(pageRef.current);

    // Images and webfonts land after first paint and shift every rule below
    // them. Each settlement gets one remeasure on the following frame.
    const images = Array.from(pageRef.current?.querySelectorAll('img') ?? []);
    images.forEach(img => {
      if (img.complete) return;
      img.addEventListener('load', soon);
      img.addEventListener('error', soon);
    });
    const fonts = (document as Document & {
      fonts?: FontFaceSet;
    }).fonts;
    fonts?.ready.then(() => live && soon());
    return () => {
      live = false;
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      window.removeEventListener('resize', debounced);
      document.removeEventListener(REMEASURE_EVENT, now);
      images.forEach(img => {
        img.removeEventListener('load', soon);
        img.removeEventListener('error', soon);
      });
      observer?.disconnect();
    };
  }, [measure, pageRef, routeKey]);
  return <>
      {points.map(p => <span className="xh" key={`${p.x.toFixed(2)}|${p.y.toFixed(2)}`} style={{
      left: p.x,
      top: p.y
    }} aria-hidden="true" data-id="element-36" data-mp="3u4g466ulj0z7" />)}
    </>;
}